import { useCallback, useEffect, useRef, useState } from "react";
import GroupementAssignDropdown from "./GroupementAssignDropdown";
import GroupingConfirmModal from "./GroupingConfirmModal";
import "./GroupingsView.css";
import "./WordListBox.css";

const VISIBLE_WORDS = 6;
const WORD_ROW_HEIGHT = 40;

export default function GroupingReassignWordsModal({
  open = false,
  words = [],
  groupements = [],
  sourceGroupementId = "",
  busy = false,
  onConfirm = async () => {},
  onClose = () => {},
}) {
  const listRef = useRef(null);
  const [selected, setSelected] = useState(() => new Set());
  const [targets, setTargets] = useState(() => ({}));
  const [scrollFades, setScrollFades] = useState({ top: false, bottom: false });

  const entries = (Array.isArray(words) ? words : [])
    .map((item) => (typeof item === "string" ? item : item?.mot))
    .map((mot) => String(mot || "").trim())
    .filter(Boolean);

  const updateScrollFades = useCallback(() => {
    const el = listRef.current;
    if (!el) {
      setScrollFades({ top: false, bottom: false });
      return;
    }
    const threshold = 2;
    setScrollFades({
      top: el.scrollTop > threshold,
      bottom: el.scrollTop + el.clientHeight < el.scrollHeight - threshold,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setTargets({});
    const frame = requestAnimationFrame(updateScrollFades);
    return () => cancelAnimationFrame(frame);
  }, [open, words, updateScrollFades]);

  const toggleMot = (mot) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(mot)) next.delete(mot);
      else next.add(mot);
      return next;
    });
    setTargets((current) => {
      if (!selected.has(mot)) return current;
      const next = { ...current };
      delete next[mot];
      return next;
    });
  };

  const handleTargetChange = (mot, groupementId) => {
    setTargets((current) => ({ ...current, [mot]: groupementId || "" }));
  };

  const affectations = entries
    .filter((mot) => selected.has(mot))
    .map((mot) => ({
      mot,
      groupementId: String(targets[mot] || "").trim(),
    }));

  const allTargetsChosen = affectations.every(
    (item) => item.groupementId && item.groupementId !== sourceGroupementId
  );
  const selectedCount = selected.size;
  const canSubmit = selectedCount > 0 && allTargetsChosen && !busy;
  const selectedCountLabel =
    selectedCount <= 1
      ? `${selectedCount} mot à réaffecter`
      : `${selectedCount} mots à réaffecter`;

  const handleConfirm = () => {
    if (!canSubmit) return;
    onConfirm(affectations);
  };

  return (
    <GroupingConfirmModal
      open={open}
      title="Réaffecter des mots du groupement"
      confirmLabel="Valider"
      cancelLabel="Annuler"
      busy={busy}
      confirmDisabled={!canSubmit}
      className="grouping-confirm-modal--reassign"
      actionsStart={
        <p className="grouping-remove-words__count">{selectedCountLabel}</p>
      }
      onConfirm={handleConfirm}
      onClose={onClose}
    >
      <p className="grouping-remove-words__intro">
        Cochez un ou plusieurs mots, puis choisissez leur nouveau groupement
        thématique.
      </p>
      <div
        className={[
          "grouping-picker-frame grouping-reassign-words__frame",
          scrollFades.top ? "grouping-picker-frame--fade-top" : "",
          scrollFades.bottom ? "grouping-picker-frame--fade-bottom" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ height: VISIBLE_WORDS * WORD_ROW_HEIGHT + 2 }}
      >
        <div
          ref={listRef}
          className={`listbox custom-scroll grouping-remove-words__list grouping-reassign-words__list${
            entries.length > VISIBLE_WORDS ? " grouping-remove-words__list--overflow" : ""
          }`}
          onScroll={updateScrollFades}
          role="listbox"
          aria-multiselectable="true"
          aria-label="Mots à réaffecter"
        >
          {entries.length === 0 ? (
            <p className="groupings-detail-empty groupings-detail-empty--in-frame">
              Il n&apos;y a actuellement aucun mot affilié à ce groupement
              thématique.
            </p>
          ) : (
            entries.map((mot) => {
              const checked = selected.has(mot);
              return (
                <div
                  key={mot}
                  className={`grouping-reassign-words__row${
                    checked ? " grouping-reassign-words__row--checked" : ""
                  }`}
                >
                  <label className="grouping-reassign-words__choice">
                    <input
                      type="checkbox"
                      className="grouping-remove-words__input"
                      checked={checked}
                      onChange={() => toggleMot(mot)}
                      disabled={busy}
                    />
                    <span className="grouping-remove-words__marker" aria-hidden="true" />
                    <span className="grouping-remove-words__label">{mot}</span>
                  </label>
                  <div
                    className={`grouping-reassign-words__assign${
                      checked ? "" : " grouping-reassign-words__assign--idle"
                    }`}
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <GroupementAssignDropdown
                      groupements={groupements}
                      value={targets[mot] || ""}
                      onChange={(next) => handleTargetChange(mot, next)}
                      disabled={busy || !checked}
                      allowNone={false}
                      excludeGroupementId={sourceGroupementId}
                      ariaLabel={`Nouveau groupement pour ${mot}`}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </GroupingConfirmModal>
  );
}
